import { useState } from "react";

/**
 * The form "Split here" opens: name the meeting that will hold everything from this
 * turn on, then hand off to the new page. The meeting being split keeps the turns
 * before this point and its own address; there is nothing to navigate to for it.
 */
export function SplitMeetingForm({
  fromSequence,
  onCancel,
  onSplit,
}: {
  fromSequence: number;
  onCancel: () => void;
  onSplit: (input: { title: string; description?: string; fromSequence: number }) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setProblem("Give the new meeting a name.");
      return;
    }

    setBusy(true);
    setProblem(null);

    try {
      await onSplit({ title: trimmed, description: description.trim() || undefined, fromSequence });
    } catch {
      setBusy(false);
      setProblem("That split could not be made. Try again.");
    }
  };

  return (
    <div className="ml-[62px] mt-[var(--space-3)] flex flex-col gap-[var(--space-3)] rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-glass)] p-[var(--space-4)]">
      <p className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
        Split into a new meeting from here
      </p>

      <input
        autoFocus
        type="text"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="Meeting name"
        className="h-9 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
      />

      <textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="Description (optional)"
        rows={2}
        className="resize-none rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-3 py-2 text-[length:var(--text-sm)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
      />

      {problem && <p className="text-[length:var(--text-xs)] text-[var(--danger)]">{problem}</p>}

      <div className="flex items-center gap-[var(--space-3)]">
        <button
          type="button"
          disabled={busy}
          onClick={() => void submit()}
          className="inline-flex h-8 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] px-3 text-[length:var(--text-sm)] font-medium text-[var(--primary-foreground)] transition hover:brightness-110 disabled:opacity-50"
        >
          {busy ? "Splitting…" : "Split"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="text-[length:var(--text-sm)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
